<script setup lang="ts">
/* eslint-disable vue/no-mutating-props */
/**
 * PracticeOverlay.vue — Extracted from PracticeView.vue Phase 6a
 *
 * Contains the practice overlay sheet with sentence navigation (First/Prev/Surround/Next/Last),
 * auto-advance switch, AnnotatedText with Popover, read order radios, manual segment jump,
 * start/stop/jump back buttons, and voice sensitivity/silence patience sliders.
 * Receives parent's reactive state object to avoid prop drilling.
 */
import type { BarMitzvahType, SentenceType } from 'types/types'
import { computed, type ComputedRef } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import AnnotatedText from '@/components/AnnotatedText.vue'
import Popover from '@/components/PopOver.vue'
import { useFont } from '@/composables/font'
import { useAnnotations } from '@/composables/useAnnotations'
import { isJumpBackDisabled, PlayPhase, ReadOrder, studentParticipates } from '@/composables/usePracticeFSM'
import {
  PRACTICE_TEXT_NAME,
  REPEATS,
  SILENCE_PATIENCE_MAX,
  SILENCE_PATIENCE_MIN,
  TOOLTIP_OPEN_DELAY_MS,
  VOICE_SENSITIVITY_MAX,
  VOICE_SENSITIVITY_MIN,
} from '@/constants/practiceConstants'
import type { AnnotatedSegment } from '@/util/AnnotatedSegment'
import type { AnnotatedSentence } from '@/util/AnnotatedSentence'
import type { PitchShiftType, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import type { TransformationType } from '@/util/Transformations'
import { inSingMode, type VoiceStyleSet } from '@/util/VoiceStyles'

const logger = useLogger()

const READ_ORDER_OPTIONS = [
  { title: 'Student only', value: ReadOrder.STUDENT_ONLY },
  { title: 'Student first, then teacher', value: ReadOrder.STUDENT_TEACHER },
  { title: 'Teacher first, then student', value: ReadOrder.TEACHER_STUDENT },
  { title: 'Teacher only', value: ReadOrder.TEACHER_ONLY },
]

// {{{ interfaces
interface PracticeOverlayState {
  focusSentenceIndex: number | null
  annotatedSentences: Array<unknown> // Array<AnnotatedSentence> — typed as unknown[] to avoid reactive() type expansion mismatch
  surroundLines: number
  autoAdvance: boolean
  tikkunStyle: boolean
  fontSize: number
  popoverVisible: boolean
  popoverShowStartNow: boolean
  inRepeatMode: boolean
  requiredRepeats: number
  popoverSentence: unknown // AnnotatedSentence | null — typed as unknown to avoid reactive() type expansion mismatch
  popoverSentenceIndex: number | null
  popoverSegmentIndex: number | null
  pitchShift: PitchShiftType
  readOrder: ReadOrder
  manualSegmentJump: boolean
  recordButtonPressed: boolean
  recordMe: boolean
  downloading: boolean
  backgroundSampling: boolean
  playPhase: PlayPhase
  finalizerHandler: NodeJS.Timeout | null
  voiceSensitivity: number
  silencePatience: number
  voiceStyleSet: VoiceStyleSet
  transformation: TransformationType | null
}

interface OverlayNavActions {
  advanceToSentence: (target: number, absolute?: boolean) => void
  closePracticeOverlay: () => void
  hasRecordingBySentenceIndex: (idx: number) => boolean
  handleSegmentClick: (
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ) => void
  handleSegmentBoundaryClick: (
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ) => boolean
  closePopover: () => void
}

interface OverlaySessionActions {
  toggleRecord: () => void
  setVoiceSensitivity: (value: number) => void
  setSilencePatience: (value: number) => void
}

interface OverlayAudioActions {
  unloaded: () => boolean
  paused: ComputedRef<boolean>
  loaded: () => boolean
  playing: ComputedRef<boolean>
  pause: () => void
  play: () => void
  setLoadPlay: (params: {
    segmentIndex: number
    pitchShift: PitchShiftType
    sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios
  }) => void
}

interface OverlayFSMActions {
  handleJumpBack: () => void
}

const props = defineProps<{
  state: PracticeOverlayState
  nav: OverlayNavActions
  session: OverlaySessionActions
  audio: OverlayAudioActions
  fsm: OverlayFSMActions
  // Computed values
  isRecording: boolean
  sentence: SentenceType // non-null: parent v-if="!!sentence" guarantees this
  sentencesWithSurround: Array<SentenceType>
  surroundBeforeStartIndex: number
  surroundOptions: Array<number>
  popoverTarget: string
  recordingsInStyle: Record<string, SentenceRecordingMetadataWithPitchedAudios[]>
  sentencesHaveSameResponsiveness: boolean
  currentSegmentIndex: number
  segmentations: Array<Array<number>>
  lineHeight: number
  barMitzvah: BarMitzvahType | null
  // Style functions
  sentenceStyle: (id: string, annotatedSentence: AnnotatedSentence) => string
  segmentBoundaryStyle: (
    style: string,
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ) => string
  segmentStyle: (
    style: string,
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ) => string
  clickableSentence: (annotatedSentence: AnnotatedSentence, sentenceIndex: number) => boolean
  clickableSegment: (
    annotatedSentence: AnnotatedSentence,
    annotatedSegment: AnnotatedSegment,
    sentenceIndex: number,
    segmentIndex: number,
  ) => boolean
  // Constants
  nextKey: string
}>()

// --- Font & Annotations ---
const { font } = useFont()
const { annotations } = useAnnotations(computed(() => props.barMitzvah))

// Destructure action objects for template compatibility
const {
  advanceToSentence,
  closePopover,
  closePracticeOverlay,
  handleSegmentBoundaryClick,
  handleSegmentClick,
  hasRecordingBySentenceIndex,
} = props.nav
const { setSilencePatience, setVoiceSensitivity, toggleRecord } = props.session
const { loaded, pause, paused, play, playing, setLoadPlay, unloaded } = props.audio
const { handleJumpBack } = props.fsm

// {{{ start/stop handlers
function handleStartClick(recordMe: boolean) {
  props.state.recordMe = recordMe
  toggleRecord()
  props.state.recordButtonPressed = true
}

function handleStopClick() {
  toggleRecord()
  props.state.recordButtonPressed = false
}
// }}}

// {{{ popover handlers
/** Playback parameters for the popover's segment, or null (after logging) when the popover isn't fully targeted. */
function popoverLoadPlayParams(): Parameters<typeof setLoadPlay>[0] | null {
  if (!props.state.popoverSentence) {
    logger.error('unexpected null state.popoverSentence')
    return null
  }
  if (props.state.popoverSentenceIndex === null) {
    logger.error('unexpected null state.popoverSentenceIndex')
    return null
  }
  if (props.state.popoverSegmentIndex === null) {
    logger.error('unexpected null state.popoverSegmentIndex')
    return null
  }
  return {
    pitchShift: props.state.pitchShift,
    segmentIndex: props.state.popoverSegmentIndex,
    sentenceRecordingMetadataWithPitchedAudios:
      props.recordingsInStyle[(props.state.popoverSentence as AnnotatedSentence).sentence().id][0],
  }
}

function handlePopoverPlay() {
  const params = popoverLoadPlayParams()
  if (!params) {
    return
  }
  if (!loaded()) {
    setLoadPlay(params)
  } else {
    play()
  }
}

function handlePopoverPause() {
  if (!props.state.popoverSentence) {
    logger.error('unexpected null state.popoverSentence')
    return
  }
  if (playing.value) {
    pause()
  }
}

function handlePopoverRestart() {
  if (playing.value) {
    pause()
  }
  const params = popoverLoadPlayParams()
  if (params) {
    setLoadPlay(params)
  }
}
// }}}
// }}}
</script>

<template>
  <v-row class="mt-4">
    <v-col lg="12" md="12" xl="10">
      <v-card elevation="2">
        <v-card-text class="pa-4">
          <!-- overlay practice text line -->
          <v-row align="center" justify="center">
            <v-col align="left" md="4">
              <v-btn
                class="mr-1"
                :disabled="isRecording || !hasRecordingBySentenceIndex(0) || state.focusSentenceIndex === 0"
                size="small"
                @click="advanceToSentence(0, true)"
              >
                <v-icon color="primary" left size="small">mdi-page-first</v-icon>
                First
              </v-btn>
              <v-btn
                v-if="state.focusSentenceIndex !== null"
                :disabled="isRecording || !hasRecordingBySentenceIndex(state.focusSentenceIndex - 1)"
                size="small"
                @click="advanceToSentence(-1)"
              >
                <v-icon color="primary" left size="small">mdi-skip-backward</v-icon>
                Prev
              </v-btn>
            </v-col>

            <v-col class="d-flex align-center ga-2" md="3">
              <span class="text-caption text-medium-emphasis text-no-wrap">Surround</span>
              <v-slider
                v-model="state.surroundLines"
                color="primary"
                hide-details
                :max="surroundOptions.length - 1"
                :min="0"
                :step="1"
                thumb-label
              />
            </v-col>

            <v-col md="4">
              <div class="d-flex align-center justify-end ga-2">
                <v-btn
                  v-if="state.focusSentenceIndex !== null"
                  :disabled="isRecording || !hasRecordingBySentenceIndex(state.focusSentenceIndex + 1)"
                  size="small"
                  @click="advanceToSentence(1)"
                >
                  Next
                  <v-icon color="primary" right size="small">mdi-skip-forward</v-icon>
                </v-btn>
                <v-btn
                  :disabled="
                    isRecording ||
                    !hasRecordingBySentenceIndex(state.annotatedSentences.length - 1) ||
                    state.focusSentenceIndex === state.annotatedSentences.length - 1
                  "
                  size="small"
                  @click="advanceToSentence(state.annotatedSentences.length - 1, true)"
                >
                  Last
                  <v-icon color="primary" right size="small">mdi-page-last</v-icon>
                </v-btn>
                <v-tooltip
                  v-if="state.focusSentenceIndex !== null"
                  bottom
                  :open-delay="TOOLTIP_OPEN_DELAY_MS"
                  text="Automatically advance to the next sentence."
                >
                  <template #activator="{ props: activatorProps }">
                    <v-btn
                      v-bind="activatorProps"
                      :active="state.autoAdvance"
                      color="primary"
                      :disabled="!hasRecordingBySentenceIndex(state.focusSentenceIndex + 1)"
                      size="small"
                      :variant="state.autoAdvance ? 'flat' : 'outlined'"
                      @click="state.autoAdvance = !state.autoAdvance"
                    >
                      <v-icon left size="small">mdi-skip-forward-outline</v-icon>
                      Auto
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>
            </v-col>
            <v-col class="d-flex justify-end" md="1">
              <v-btn icon size="small" variant="text" @click="closePracticeOverlay">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-row class="my-6" justify="center">
            <v-col class="text-center" md="11">
              <span ref="practiceSpan">
                <annotated-text
                  v-if="barMitzvah"
                  :annotations="annotations"
                  :break-before-line="
                    (): boolean => {
                      if (state.tikkunStyle) {
                        return false
                      }
                      return !sentencesHaveSameResponsiveness || state.surroundLines !== 0
                    }
                  "
                  :clickable-segment="clickableSegment"
                  :clickable-sentence="clickableSentence"
                  :default-font="font"
                  :font-size="state.fontSize"
                  :line-height="lineHeight"
                  :name="PRACTICE_TEXT_NAME"
                  :segment-boundary-style="segmentBoundaryStyle"
                  :segment-style="segmentStyle"
                  :segmentations="segmentations"
                  :sentence-style="sentenceStyle"
                  :sentences="sentencesWithSurround"
                  :show-end-of-sentence-character="!state.tikkunStyle"
                  :show-responsive-headers="
                    () => {
                      return !sentencesHaveSameResponsiveness
                    }
                  "
                  :show-sentence-numbers="!state.tikkunStyle"
                  :start-index="surroundBeforeStartIndex"
                  :suppress-colors="
                    () => {
                      return state.tikkunStyle
                    }
                  "
                  :transformation="state.transformation as TransformationType"
                  @click-segment="handleSegmentClick"
                  @click-segment-boundary="handleSegmentBoundaryClick"
                />
              </span>

              <popover :attach-id="popoverTarget" :visible="state.popoverVisible">
                <v-card>
                  <v-card-text class="pa-1">
                    <!-- "Sing Now!" popover -->
                    <template v-if="state.popoverShowStartNow">
                      <span class="mx-4 my-5 text-h5">{{ inSingMode(state.voiceStyleSet) ? 'Sing' : 'Read' }} now!</span>
                    </template>
                    <!-- repeat popover -->
                    <template v-else-if="state.inRepeatMode">
                      <div class="pa-3 ma-3 text-h2">
                        {{ state.requiredRepeats }}
                      </div>
                    </template>
                    <template v-else>
                      <v-icon v-if="unloaded() || paused" class="playable" size="xxx-large" @click="handlePopoverPlay"
                        >mdi-play mdi-rotate-180</v-icon
                      >
                      <v-icon v-else class="pausable" size="xxx-large" @click="handlePopoverPause">mdi-pause</v-icon>

                      <v-icon size="xxx-large" @click="handlePopoverRestart">mdi-skip-forward</v-icon>

                      <v-icon size="xxx-large" @click="closePopover()">mdi-close</v-icon>
                    </template>
                  </v-card-text>
                </v-card>
              </popover>
            </v-col>
          </v-row>

          <v-row align="center" justify="center">
            <v-col md="3" offset-md="1">
              <v-select
                v-model="state.readOrder"
                density="compact"
                :disabled="isRecording"
                hide-details
                item-title="title"
                item-value="value"
                :items="READ_ORDER_OPTIONS"
                label="Practice mode"
                variant="underlined"
              />
            </v-col>
            <v-col class="d-flex align-center" md="4" offset-md="1">
              <v-tooltip
                bottom
                :open-delay="TOOLTIP_OPEN_DELAY_MS"
                :text="`Disables automatic word or segment jumps; use the ['${nextKey}'] key to jump to the next segment.`"
              >
                <template #activator="{ props: activatorProps }">
                  <v-btn
                    v-bind="activatorProps"
                    :active="state.manualSegmentJump"
                    color="primary"
                    :disabled="sentence.isResponsive"
                    size="small"
                    :variant="state.manualSegmentJump ? 'flat' : 'outlined'"
                    @click="state.manualSegmentJump = !state.manualSegmentJump"
                  >
                    <v-icon left size="small">mdi-debug-step-over</v-icon>
                    Manual step
                  </v-btn>
                </template>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row justify="center">
            <v-col class="text-center" md="3">
              <div v-if="!isRecording" class="d-flex ga-2">
                <v-btn
                  class="flex-grow-1"
                  color="primary"
                  :disabled="sentence === null || state.downloading"
                  variant="outlined"
                  @click="handleStartClick(false)"
                >
                  <v-icon left>mdi-play</v-icon>
                  Practice
                </v-btn>
                <v-btn
                  class="flex-grow-1"
                  color="primary"
                  :disabled="sentence === null || state.downloading || !studentParticipates(state.readOrder)"
                  @click="handleStartClick(true)"
                >
                  <v-icon left>mdi-microphone</v-icon>
                  Record
                </v-btn>
              </div>
              <v-btn
                v-else
                block
                color="warning"
                :disabled="isJumpBackDisabled(state.recordMe, state.backgroundSampling, currentSegmentIndex, state.playPhase)"
                @click="handleJumpBack"
              >
                >> Jump back, practice {{ REPEATS }}x
              </v-btn>
            </v-col>
            <v-col class="text-center" md="1">
              <v-progress-circular v-if="state.downloading || state.finalizerHandler" color="primary" indeterminate />
              <v-icon
                v-show="!state.finalizerHandler && !state.downloading && isRecording && !state.backgroundSampling"
                color="red"
                label="Recording..."
                size="xxx-large"
                >mdi-microphone</v-icon
              >
            </v-col>
            <v-col md="3">
              <v-btn block class="error" :disabled="!isRecording || sentence === null" @click="handleStopClick">
                <v-icon left>mdi-stop</v-icon>
                Stop
              </v-btn>
            </v-col>
          </v-row>

          <v-row class="my-3 py-3" justify="center">
            <v-col md="3">
              Voice sensitivity
              <v-slider
                v-model="state.voiceSensitivity"
                color="primary"
                dense
                :disabled="sentence.isResponsive"
                hide-details
                :max="VOICE_SENSITIVITY_MAX"
                :min="VOICE_SENSITIVITY_MIN"
                thumb-label
                @change="setVoiceSensitivity"
              ></v-slider>
            </v-col>
            <v-col md="1" />
            <v-col md="3">
              Post-silence pause patience
              <v-slider
                v-model="state.silencePatience"
                color="primary"
                dense
                :disabled="sentence.isResponsive"
                hide-details
                :max="SILENCE_PATIENCE_MAX"
                :min="SILENCE_PATIENCE_MIN"
                thumb-label
                @change="setSilencePatience"
              ></v-slider>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>
